import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useMotionTemplate } from "framer-motion";
import { MaskText } from "@/components/motion";
import { CtaButton } from "@/components/CtaButton";
import { Countdown } from "@/components/landing/Countdown";
import { HeroVideo } from "@/components/landing/HeroVideo";
import { LOGO, EVENT_TIME, SEATS_LEFT } from "@/lib/content";

const EASE = [0.22, 1, 0.36, 1];
const fade = (d) => ({ initial: { opacity: 0, y: 18 }, animate: { opacity: 1, y: 0 }, transition: { duration: 1, ease: EASE, delay: d } });
const CHIPS = ["25 e 26 novembre", `ore ${EVENT_TIME}`, "Online su Zoom", "€27 + IVA"];
const LINES = [
  <span className="gold-text">Natural Anti-Age ed</span>,
  <span className="gold-text">estetica rigenerativa:</span>,
  "il metodo che ha già",
  "formato oltre 580 centri.",
  <em className="font-medium">Ora te lo mostriamo dal vivo.</em>,
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const vy = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const ty = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const sx = useMotionValue(70);
  const sy = useMotionValue(30);
  const spot = useMotionTemplate`radial-gradient(600px circle at ${sx}% ${sy}%, rgba(199,154,75,0.16), transparent 65%)`;
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    sx.set(((e.clientX - r.left) / r.width) * 100);
    sy.set(((e.clientY - r.top) / r.height) * 100);
  };

  return (
    <header ref={ref} id="hero" onMouseMove={onMove} className="relative overflow-hidden px-5 pb-20 pt-6 sm:px-8 lg:px-12 lg:pb-28" data-testid="hero-section">
      <motion.div className="pointer-events-none absolute inset-0" style={{ background: spot }} />
      <div className="pointer-events-none absolute -right-40 -top-40 h-[640px] w-[640px] rounded-full bg-gold/10 blur-[140px]" />
      <div className="wrap relative">
        <motion.div {...fade(0)} className="flex items-center justify-between">
          <img src={LOGO} alt="Skin Lovers Professional" className="h-5 w-auto sm:h-7" data-testid="header-logo" />
          <span className="hidden text-[11px] font-semibold uppercase tracking-[0.24em] text-sand sm:block">Workshop · Novembre 2026</span>
        </motion.div>
        <div className="line-draw mt-6 h-px w-full bg-gradient-to-r from-gold/70 via-gold/20 to-transparent" />
        <div className="mt-12 grid items-center gap-16 lg:mt-16 lg:grid-cols-[1.4fr_0.6fr] lg:gap-10">
          <motion.div style={{ y: ty }}>
            <motion.p {...fade(0.2)} className="eyebrow">Natural Anti-Age · Riservato alle titolari · 2 serate su Zoom</motion.p>
            <MaskText as="h1" onLoad delay={0.35} lines={LINES} className="mt-6 text-[2.35rem] leading-[1] text-cream sm:text-6xl lg:text-[4.1rem] xl:text-[4.4rem]" data-testid="hero-title" />
            <motion.p {...fade(1.1)} className="lead mt-8 max-w-xl">
              Due serate con Valentina e Renata per scoprire il Natural Anti-Age e l'estetica rigenerativa non invasiva, guardare i risultati dei nostri protocolli e capire come può far crescere il fatturato del tuo centro.
            </motion.p>
            <motion.div {...fade(1.25)} className="mt-7 flex flex-wrap gap-2" data-testid="hero-chips">
              {CHIPS.map((c) => <span key={c} className="chip">{c}</span>)}
            </motion.div>
            <motion.div {...fade(1.4)} className="mt-8"><Countdown /></motion.div>
            <motion.div {...fade(1.55)} className="mt-9 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
              <CtaButton testid="hero-cta-btn" />
              <span className="text-sm text-sand" data-testid="hero-seats">
                Posti limitati{SEATS_LEFT ? <> · <b className="text-gold-light">restano {SEATS_LEFT} posti</b></> : null}
              </span>
            </motion.div>
          </motion.div>
          <HeroVideo parallaxY={vy} />
        </div>
      </div>
    </header>
  );
};
